import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';

import { CityIcon, FriendsIcon, GroupsIcon } from '@treams/treams-icons';
import { Highlight, Paragraph, Popover } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import Confirm from './Confirm';
import Delete from './Delete';
import Outlet from './Outlet';

const Name = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Footnote = styled(Paragraph)`
	font-size: 13px;
	line-height: 15px;
`;

const GroupContainer = styled(Flex)<{ $enabled?: boolean; $selected?: boolean; $color: string }>`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 0px 0px 1px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)},
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)};
	width: 160px;
	height: 40px;
	padding: 0px 10px;
	font-size: 14px;
	line-height: 14px;
	align-items: center;
	border-left: 4px solid ${(props) => props.theme.colors[props.$color]};

	${(props) =>
		props.$enabled !== false &&
		`
	cursor: pointer;

	&:hover {
		box-shadow: 0px 0px 0px 1px ${rgba(props.theme.colors.slateBlue, 0.5)},
			0px 2px 4px ${rgba(props.theme.colors.slateBlue, 0.25)};
	}`}

	${(props) =>
		props.$selected &&
		`
		${Footnote} {
			color: ${rgba(props.theme.colors.white, 0.5)};
		}
		
		&, &:hover {
			background: ${props.theme.colors.primary};
			color: ${props.theme.colors.white};
			border-left-color: ${rgba(props.theme.colors.white, 0.5)};
			box-shadow: 0px 0px 0px 2px ${rgba(props.theme.colors.primary, 0.25)};
		}
	`}
`;

const Icon = styled(Box)<{ $selected?: boolean }>`
	svg {
		display: block;
		width: 20px;
		height: 20px;

		path {
			fill: ${(props) =>
				props.$selected ? rgba(props.theme.colors.white, 0.5) : props.theme.colors.slateBlue};
		}
	}
`;

export default function Group(props: {
	group: graphql.Group;
	query: string;
	parent?: boolean;
	hasChildren: boolean;
	recursive?: boolean;
	inlet?: boolean;
	outlet?: boolean;
	single: boolean;
	canSelect: boolean;
	onSelect?: (recursive: boolean) => void;
	onDeselect?: () => void;
}) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState(false);

	return (
		<Popover.Target
			visible={selected}
			contents={
				<>
					{props.onSelect && !props.onDeselect && (
						<Confirm
							single={props.single}
							canSelect={props.canSelect}
							group={props.group}
							hasChildren={props.hasChildren}
							onSelect={(recursive) => {
								props.onSelect!(recursive);
								setSelected(false);
							}}
						/>
					)}
					{props.onDeselect && (
						<Delete
							single={props.single}
							group={props.group}
							recursive={props.recursive}
							onSelect={(recursive) => {
								props.onSelect!(recursive);
								setSelected(false);
							}}
							onDeselect={() => {
								props.onDeselect!();
								setSelected(false);
							}}
						/>
					)}
				</>
			}
			onDismiss={() => setSelected(false)}
		>
			{props.inlet && <Outlet top={0} selected={selected} />}
			<GroupContainer
				onClick={() => setSelected(true)}
				$selected={selected}
				$color={
					props.parent
						? props.group.type === graphql.GroupType.Department
							? 'employee'
							: 'manager'
						: 'primary'
				}
			>
				<Box flexGrow={1} flexShrink={1}>
					<Name>
						<Highlight query={props.query}>{props.group.name}</Highlight>
					</Name>
					{props.recursive && <Footnote>+ {t('groups.selector.group.underlying')}</Footnote>}
				</Box>
				<Icon $selected={selected} flexGrow={0} flexShrink={0} ml={2}>
					{!props.parent && <CityIcon />}
					{props.parent && props.group.type === graphql.GroupType.Department && <GroupsIcon />}
					{props.parent && props.group.type === graphql.GroupType.Team && <FriendsIcon />}
				</Icon>
			</GroupContainer>
			{props.outlet && <Outlet bottom={0} selected={selected} />}
		</Popover.Target>
	);
}

Group.Skeleton = function GroupSkeleton() {
	return (
		<GroupContainer $color="slateBlue" $enabled={false}>
			<Box flexGrow={1} flexShrink={1}>
				<Paragraph.Skeleton lines={1} width={136} />
			</Box>
		</GroupContainer>
	);
};
